import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef
} from 'react'
import theme from '@evocolabs/orkaui/lib/theme.cjs'
import { useBackend } from '@/src/providers/BackendProvider'

const hr2ms = 60 * 60 * 1000
let availableTime = []

function convertTZ(time, type, offset) {
  const convertTime = time - offset
  if (type === 'start') {
    if (convertTime < 0) {
      return convertTime + 24
    } else if (convertTime >= 24) {
      return convertTime - 24
    } else {
      return convertTime
    }
  } else {
    if (convertTime <= 0) {
      return convertTime + 24
    } else if (convertTime > 24) {
      return convertTime - 24
    } else {
      return convertTime
    }
  }
}

function roundToHour(date) {
  return new Date(Math.round(date.getTime() / hr2ms) * hr2ms)
}

function floorToHour(date) {
  return new Date(new Date(date).setMinutes(0, 0, 0))
}

function floorToDate(date) {
  return new Date(date).setHours(0, 0, 0, 0)
}

function CalendarProvider(props) {
  const { getAvailableTimeslots } = useBackend()
  const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone
  const TZOffset = new Date(Date.now()).getTimezoneOffset() / 60
  const dayList = [
    {
      name: 'Sun',
      color: theme.palette.primary[600]
    },
    {
      name: 'Mon',
      color: theme.palette.grey[700]
    },
    {
      name: 'Tue',
      color: theme.palette.grey[700]
    },
    {
      name: 'Wed',
      color: theme.palette.grey[700]
    },
    {
      name: 'Thu',
      color: theme.palette.grey[700]
    },
    {
      name: 'Fri',
      color: theme.palette.grey[700]
    },
    {
      name: 'Sat',
      color: theme.palette.primary[600]
    }
  ]
  const monthList = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ]

  const timeOfDay = [
    [
      {
        start: convertTZ(1.5, 'start', TZOffset),
        end: convertTZ(2, 'end', TZOffset)
      },
      {
        start: convertTZ(2, 'start', TZOffset),
        end: convertTZ(2.5, 'end', TZOffset)
      },
      {
        start: convertTZ(2.5, 'start', TZOffset),
        end: convertTZ(3, 'end', TZOffset)
      },
      {
        start: convertTZ(3, 'start', TZOffset),
        end: convertTZ(3.5, 'end', TZOffset)
      }
    ],
    [
      {
        start: convertTZ(1.5, 'start', TZOffset),
        end: convertTZ(2, 'end', TZOffset)
      },
      {
        start: convertTZ(2, 'start', TZOffset),
        end: convertTZ(2.5, 'end', TZOffset)
      },
      {
        start: convertTZ(2.5, 'start', TZOffset),
        end: convertTZ(3, 'end', TZOffset)
      },
      {
        start: convertTZ(3, 'start', TZOffset),
        end: convertTZ(3.5, 'end', TZOffset)
      },
      {
        start: convertTZ(12, 'start', TZOffset),
        end: convertTZ(12.5, 'end', TZOffset)
      },
      {
        start: convertTZ(12.5, 'start', TZOffset),
        end: convertTZ(13, 'end', TZOffset)
      },
      {
        start: convertTZ(13, 'start', TZOffset),
        end: convertTZ(13.5, 'end', TZOffset)
      },
      {
        start: convertTZ(13.5, 'start', TZOffset),
        end: convertTZ(14, 'end', TZOffset)
      }
    ]
  ]

  // possible time list in UTC time and converted to brower timezone.
  const [possibleTime, setPossibleTime] = useState(timeOfDay[1])
  const possibleTimeRef = useRef()
  possibleTimeRef.current = possibleTime

  const [date, setDate] = useState({
    now: Date.now(),
    expiration: floorToDate(
      new Date(Date.now()).setDate(new Date(Date.now()).getDate() + 15)
    )
  })
  const [calendarData, setCalendarData] = useState({
    date: new Date(date.now).getDate(),
    month: new Date(date.now).getMonth(),
    year: new Date(date.now).getFullYear()
  })
  const [_selection, _setSelection] = useState({
    date: null,
    month: null,
    year: null
  })
  const [dateList, setDateList] = useState(
    Array.from(
      {
        length: new Date(calendarData.year, calendarData.month + 1, 0).getDate()
      },
      (_, i) => i + 1
    )
  )
  const [availableTimeSlots, setAvailableTimeSlots] = useState([])
  const [availableSlots, setAvailableSlots] = useState(
    Array.from({ length: 4 }, () => true)
  )
  useEffect(() => {
    ;(async () => {
      const res = await getAvailableTimeslots(date)
      setAvailableTimeSlots(res.data.results)
    })()
  }, [date])

  useEffect(() => {
    setDateList(
      Array.from(
        {
          length: new Date(
            calendarData.year,
            calendarData.month + 1,
            0
          ).getDate()
        },
        (_, i) => i + 1
      )
    )
  }, [calendarData])

  useEffect(() => {
    if (_selection.date !== null) {
      const week = new Date(
        _selection.year,
        _selection.month,
        _selection.date
      ).getDay()
      if (week === 1 || week === 5) {
        possibleTimeRef.current = timeOfDay[0]
        setPossibleTime(timeOfDay[0])
      } else {
        possibleTimeRef.current = timeOfDay[1]
        setPossibleTime(timeOfDay[1])
      }
    }
    setAvailableSlots(isDisabledTime())
  }, [_selection])

  useEffect(() => {
    availableTime = availableTimeSlots.map((item) => {
      const start = new Date(item.start_time)
      const end = new Date(item.end_time)
      return {
        date: start.getDate(),
        month: start.getMonth(),
        year: start.getFullYear(),
        start: convertTZ((start.getTime() / hr2ms) % 24, 'start', TZOffset),
        end: convertTZ((end.getTime() / hr2ms) % 24, 'end', TZOffset)
      }
    })
    setAvailableSlots(isDisabledTime())
  }, [availableTimeSlots])

  async function refreshAvailableTimeSlots() {
    const res = await getAvailableTimeslots(date)
    setAvailableTimeSlots(res.data.results)
    possibleTimeRef.current = timeOfDay[1]
    _setSelection({
      date: null,
      month: null,
      year: null
    })
  }

  function isDisabledDate(inputDate) {
    const inputDay = new Date(
      calendarData.year,
      calendarData.month,
      inputDate
    ).getDay()
    const isWeekend = inputDay % 6 === 0
    return (
      Date.UTC(calendarData.year, calendarData.month, inputDate, TZOffset) <
        floorToDate(date.now) ||
      Date.UTC(calendarData.year, calendarData.month, inputDate, TZOffset) >=
        date.expiration ||
      isWeekend
    )
  }

  function isDisabledTime() {
    const possibleTime = possibleTimeRef.current
    const list = []
    for (let i = 0; i < possibleTime.length; i++) {
      const timeObject = {
        ..._selection,
        start: possibleTime[i].start,
        end: possibleTime[i].end
      }
      list.push(
        availableTime.findIndex(
          (e) =>
            e.date === timeObject.date &&
            e.month === timeObject.month &&
            e.year === timeObject.year &&
            e.start <= timeObject.start &&
            e.end >= timeObject.end
        ) + 1
      )
    }
    return list
  }

  function isToday(inputDate) {
    const now = new Date(date.now)
    const calendarDate = new Date(
      calendarData.year,
      calendarData.month,
      inputDate
    )
    return (
      calendarDate.getFullYear() === now.getFullYear() &&
      calendarDate.getMonth() === now.getMonth() &&
      calendarDate.getDate() === now.getDate()
    )
  }

  function getDay(inputDate) {
    return new Date(calendarData.year, calendarData.month, inputDate).getDay()
  }

  function switchMonth(direction) {
    if (direction === 'back') {
      const now = new Date(calendarData.year, calendarData.month, 1)
      const last = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      setCalendarData({
        ...calendarData,
        month: last.getMonth(),
        year: last.getFullYear()
      })
    } else {
      const now = new Date(calendarData.year, calendarData.month, 1)
      const next = new Date(now.getFullYear(), now.getMonth() + 1, 1)
      setCalendarData({
        ...calendarData,
        month: next.getMonth(),
        year: next.getFullYear()
      })
    }
  }

  return (
    <CalendarContext.Provider
      value={{
        dayList,
        monthList,
        date,
        setDate,
        calendarData,
        setCalendarData,
        _selection,
        _setSelection,
        dateList,
        setDateList,
        TZ,
        possibleTime,
        availableTime,
        availableTimeSlots,
        availableSlots,
        refreshAvailableTimeSlots,
        isDisabledDate,
        isToday,
        getDay,
        switchMonth
      }}
      {...props}
    />
  )
}

const CalendarContext = createContext()
const useCalendar = () => useContext(CalendarContext)
export { CalendarProvider, useCalendar, CalendarContext, floorToHour }
